/**
 * ゼロベース再設計の理想形 audit。
 * 自前定義の trace tone (badgeTones.ts) を使って各画面に色を割り当てる。
 *
 * 設計原則:
 *   1. 通常の status は全て soft pastel
 *   2. critical-* は致命的失敗 / 緊急対応 / 成約のような重大な瞬間のみ
 *   3. 順序ステージ (CRM) は violet グラデーション
 *   4. ニュートラル分類は tag-neutral
 *   5. DaisyUI badge-* プリセットは使わない
 */

import type { BadgeTone } from "components/ui/StatusBadge";
import type { BadgeAuditScreen } from "routes/SnippetsPage/badgeAudit";

export type ProposalEntry = {
  label: string;
  tone: BadgeTone;
  reason?: string;
};

export type ProposalScreen = {
  screen: string;
  group: BadgeAuditScreen["group"];
  entries: ProposalEntry[];
};

export const badgeAuditProposal: ProposalScreen[] = [
  // ──────────── Delivery ────────────
  {
    screen: "/deliveries (一覧)",
    group: "delivery",
    entries: [
      { label: "送信完了", tone: "soft-success", reason: "完了" },
      { label: "一部失敗", tone: "soft-warning", reason: "部分失敗 = 警告" },
      {
        label: "失敗",
        tone: "critical-error",
        reason: "致命的失敗 → 注意喚起",
      },
      { label: "送信中", tone: "soft-info", reason: "進行中" },
      { label: "予約中", tone: "soft-info", reason: "未来の進行中" },
      { label: "下書き", tone: "soft-neutral", reason: "未確定" },
      { label: "キャンセル", tone: "soft-neutral", reason: "停止" },
    ],
  },
  {
    screen: "/deliveries/:id (宛先)",
    group: "delivery",
    entries: [
      { label: "送信", tone: "soft-success", reason: "完了" },
      { label: "待機", tone: "soft-neutral", reason: "未実施" },
      { label: "バウンス", tone: "critical-error", reason: "致命的・要対応" },
      { label: "苦情", tone: "critical-error", reason: "致命的・要対応" },
    ],
  },

  // ──────────── Matching ────────────
  {
    screen: "/matching/results (種別)",
    group: "matching",
    entries: [
      { label: "フリー", tone: "tag-neutral", reason: "分類タグ" },
      { label: "手動", tone: "tag-neutral", reason: "分類タグ" },
      { label: "プリセット", tone: "tag-neutral", reason: "分類タグ" },
      { label: "自動", tone: "soft-info", reason: "システム動作 (人為と区別)" },
    ],
  },
  {
    screen: "/matching/results (ソース種別)",
    group: "matching",
    entries: [
      { label: "人材", tone: "tag-neutral", reason: "ニュートラル分類" },
      { label: "案件", tone: "tag-neutral", reason: "ニュートラル分類" },
    ],
  },
  {
    screen: "/matching/results (ステータス)",
    group: "matching",
    entries: [
      { label: "新規", tone: "soft-info", reason: "未対応 = 注意喚起" },
      { label: "閲覧済", tone: "soft-neutral", reason: "中立" },
      { label: "提案済", tone: "soft-success", reason: "前進" },
      { label: "却下", tone: "soft-error", reason: "拒否 (通常レベル)" },
    ],
  },
  {
    screen: "商流深さ",
    group: "matching",
    entries: [
      { label: "商流 2以下", tone: "soft-success", reason: "良" },
      { label: "商流 3", tone: "soft-info", reason: "可" },
      { label: "商流 4", tone: "soft-warning", reason: "注意" },
      { label: "商流 5以上", tone: "soft-error", reason: "深い (悪)" },
    ],
  },

  // ──────────── CRM ────────────
  {
    screen: "/crm/talents 稼働状態",
    group: "crm",
    entries: [
      { label: "稼働中", tone: "soft-success", reason: "正常稼働" },
      { label: "もうすぐ空き", tone: "soft-warning", reason: "近日対応必要" },
      { label: "空き", tone: "soft-info", reason: "新規アクション可能" },
    ],
  },
  {
    screen: "/crm/sales-board パイプラインステージ",
    group: "crm",
    entries: [
      { label: "提案準備", tone: "stage-1", reason: "順序 1/5" },
      { label: "提案済", tone: "stage-2", reason: "順序 2/5" },
      { label: "書類選考", tone: "stage-3", reason: "順序 3/5" },
      { label: "面談", tone: "stage-4", reason: "順序 4/5" },
      { label: "オファー", tone: "stage-final", reason: "順序 最終" },
      {
        label: "成約",
        tone: "critical-success",
        reason: "重要マイルストーン → 強調",
      },
      { label: "失注", tone: "soft-neutral", reason: "終了" },
    ],
  },
  {
    screen: "/crm/sales-board 注力案件ステータス",
    group: "crm",
    entries: [
      { label: "募集中", tone: "soft-info", reason: "オープン" },
      { label: "候補あり", tone: "soft-warning", reason: "対応必要" },
      { label: "充足", tone: "soft-success", reason: "完了" },
      { label: "クローズ", tone: "soft-neutral", reason: "終了" },
    ],
  },
  {
    screen: "/crm 契約ステータス",
    group: "crm",
    entries: [
      { label: "稼働中", tone: "soft-success", reason: "正常" },
      { label: "更新待ち", tone: "soft-warning", reason: "対応必要" },
      { label: "終了", tone: "soft-neutral", reason: "終了" },
      { label: "更新済", tone: "soft-success", reason: "完了" },
    ],
  },

  // ──────────── Settings ────────────
  {
    screen: "/settings/sender-emails 認証",
    group: "settings",
    entries: [
      { label: "認証待ち", tone: "soft-warning", reason: "保留" },
      { label: "認証済み", tone: "soft-success", reason: "完了" },
      { label: "認証失敗", tone: "soft-error", reason: "失敗 (通常)" },
    ],
  },
  {
    screen: "/settings/presets",
    group: "settings",
    entries: [
      { label: "人材", tone: "tag-neutral", reason: "分類" },
      { label: "案件", tone: "tag-neutral", reason: "分類" },
      { label: "有効", tone: "soft-success", reason: "active" },
      { label: "無効", tone: "soft-neutral", reason: "非アクティブ" },
    ],
  },
  {
    screen: "/settings/matching/conditions",
    group: "settings",
    entries: [
      { label: "アクティブ", tone: "soft-success", reason: "active" },
      { label: "非アクティブ", tone: "soft-neutral", reason: "非アクティブ" },
    ],
  },
  {
    screen: "/settings/invitations",
    group: "settings",
    entries: [
      { label: "保留中", tone: "soft-warning", reason: "保留" },
      { label: "承認済み", tone: "soft-success", reason: "完了" },
      { label: "期限切れ", tone: "soft-error", reason: "失敗 (通常)" },
      { label: "キャンセル済み", tone: "soft-neutral", reason: "終了" },
    ],
  },

  // ──────────── Admin ────────────
  {
    screen: "/admin/tenants",
    group: "admin",
    entries: [
      { label: "有効", tone: "soft-success", reason: "active" },
      { label: "無効", tone: "soft-neutral", reason: "非アクティブ" },
    ],
  },
  {
    screen: "/admin/feature-flags カテゴリ",
    group: "admin",
    entries: [
      { label: "matching", tone: "tag-neutral", reason: "分類" },
      { label: "delivery", tone: "tag-neutral", reason: "分類" },
      { label: "email", tone: "tag-neutral", reason: "分類" },
      { label: "system", tone: "tag-neutral", reason: "分類" },
    ],
  },
  {
    screen: "/admin/feature-flags ON/OFF",
    group: "admin",
    entries: [
      { label: "ON", tone: "soft-success", reason: "有効" },
      { label: "OFF", tone: "soft-neutral", reason: "無効" },
    ],
  },
  {
    screen: "/admin/usage プラン状態",
    group: "admin",
    entries: [
      { label: "active", tone: "soft-success", reason: "正常" },
      { label: "trial", tone: "soft-info", reason: "情報" },
      { label: "(その他)", tone: "soft-neutral", reason: "中立" },
    ],
  },
  {
    screen: "/admin/usage 使用量",
    group: "admin",
    entries: [
      { label: "超過", tone: "critical-error", reason: "致命的・即対応" },
      { label: "警告", tone: "soft-warning", reason: "警告" },
      { label: "正常", tone: "soft-success", reason: "正常" },
    ],
  },
  {
    screen: "/admin/distribution/contacts",
    group: "admin",
    entries: [
      { label: "有効", tone: "soft-success", reason: "active" },
      { label: "無効", tone: "soft-neutral", reason: "非アクティブ" },
    ],
  },

  // ──────────── Email ────────────
  {
    screen: "EmailDetail マッチング種別",
    group: "email",
    entries: [
      { label: "手動", tone: "tag-neutral", reason: "分類" },
      { label: "自動", tone: "soft-info", reason: "システム動作" },
      { label: "プリセット", tone: "tag-neutral", reason: "分類" },
    ],
  },
  {
    screen: "EmailDetail スコア",
    group: "email",
    entries: [
      { label: "80%以上", tone: "soft-success", reason: "良" },
      { label: "60-79%", tone: "soft-warning", reason: "可" },
      { label: "60%未満", tone: "soft-error", reason: "不可" },
    ],
  },
  {
    screen: "EmailDetail 詳細比較",
    group: "email",
    entries: [
      { label: "match", tone: "soft-success", reason: "完全一致" },
      { label: "partial", tone: "soft-warning", reason: "部分一致" },
    ],
  },
  {
    screen: "メール検索 リモート/外国籍",
    group: "email",
    entries: [
      { label: "OK", tone: "soft-success", reason: "可" },
      {
        label: "NG",
        tone: "soft-neutral",
        reason: "不可 (error にしない、否定的すぎる)",
      },
    ],
  },

  // ──────────── Home / Profile ────────────
  {
    screen: "ProfilePage 権限",
    group: "home",
    entries: [
      {
        label: "システム管理者",
        tone: "soft-info",
        reason: "情報・特権 (warning にしない)",
      },
    ],
  },
];
